import axios from "axios";
import { toSnakeCase, toCamelCase } from "./data-format";
import { getToken, removeToken, setToken, getUserInfo, removeAll } from "./auth";
import { refresh } from "@/api/security";
import { Message } from "element-ui";
import router from "@/router";
import store from "@/store";

const http = axios.create({
    baseURL: process.env.VUE_APP_BASE_API,
    timeout: 30000,
});

// 定时刷新、并发锁、请求队列全局变量
let tokenRefreshTimer = null;
let isRefreshing = false;
let pendingRequestQueue = [];
// 根据后端accessToken过期时长调整，示例30分钟有效期，提前5分钟刷新
const REFRESH_INTERVAL = 25 * 60 * 1000;

/** 开启定时静默刷新token */
function startAutoRefreshToken() {
    stopAutoRefreshToken();
    tokenRefreshTimer = setInterval(async () => {
        const refreshToken = getUserInfo("refreshToken");
        const accessToken = getToken();
        if (!refreshToken || !accessToken) {
            stopAutoRefreshToken();
            return;
        }
        try {
            await refresh({ refreshToken }, { unformat: true }).then(res => {
                setToken(res.data.accessToken);
            });
        } catch (err) {
            stopAutoRefreshToken();
            clearLoginInfo();
        }
    }, REFRESH_INTERVAL);
}

/** 关闭定时刷新 */
function stopAutoRefreshToken() {
    if (tokenRefreshTimer) {
        clearInterval(tokenRefreshTimer);
        tokenRefreshTimer = null;
    }
}

/** 统一清空登录信息并跳转登录页 */
function clearLoginInfo() {
    stopAutoRefreshToken();
    removeToken();
    removeAll();
    window.sessionStorage.clear();
    pendingRequestQueue = [];
    router.push({ path: "/login" });
}

http.interceptors.request.use(
    (config) => {
        const token = getToken();
        if (token != undefined) {
            config.headers.common["Authorization"] = `Bearer ${token}`;
        }
        config.headers.common["accept-language"] = store.getters.language;
        if (!config.unformat) {
            config.params = toSnakeCase(config.params);
            config.data = toSnakeCase(config.data);
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

http.interceptors.response.use(
    (response) => {
        // 登录接口成功，启动定时刷新
        if (response.config.url?.includes("login")) {
            startAutoRefreshToken();
        }
        if (response.headers["content-type"] === "application/json") {
            response.data = toCamelCase(response.data);
        }
        return response;
    },
    async (error) => {
        if (!error.response) {
            Message.error("網路連線異常，請檢查網路");
            return Promise.reject(error);
        }

        const { status } = error.response;
        const originConfig = error.config;

        // 401 Token过期自动刷新逻辑
        if (status === 401 && !originConfig._retry) {
            const refreshToken = getUserInfo("refreshToken");
            if (!refreshToken) {
                clearLoginInfo();
                return Promise.reject(error);
            }

            if (isRefreshing) {
                return new Promise((resolve) => {
                    pendingRequestQueue.push((newToken) => {
                        originConfig.headers.Authorization = `Bearer ${newToken}`;
                        resolve(http(originConfig));
                    });
                });
            }

            isRefreshing = true;
            originConfig._retry = true;
            try {
                const res = await refresh({ refreshToken }, { unformat: true });
                const newAccessToken = res.data.accessToken;
                setToken(newAccessToken);
                pendingRequestQueue.forEach((cb) => cb(newAccessToken));
                pendingRequestQueue = [];
                originConfig.headers.Authorization = `Bearer ${newAccessToken}`;
                return http(originConfig);
            } catch {
                clearLoginInfo();
                return Promise.reject(error);
            } finally {
                isRefreshing = false;
            }
        }

        // 保留你原有全部错误处理逻辑，无改动
        else if (error.response.status === 500) {
            if (error.response.data && error.response.data.message && error.response.data.message.indexOf("($") > 0) {
                let regex = new RegExp("(?<=\\(\\$)(.+?)(?=\\))", "g");
                let key = error.response.data.message.match(regex)[0];
                if (key == "refreshToken") console.log(error.response.data.message);
                else Message.error(error.response.data.message);
            } else console.log(error.response.data.message);
        } else if (
            error.response.data.error_code == "INVALID_USER_ACCOUNT" ||
            error.response.data.error_code == "INVALID_PARENT_PASSWORD" ||
            error.response.data.error_code == "INVALID_STUDENT_PASSWORD" ||
            error.response.data.error_code == "SUBSCRIPTION_EXPIRED_ERROR" ||
            error.response.data.error_code == "NO_SUBSCRIPTION_ERROR" ||
            error.response.data.error_code == "SUBSCRIPTION_NOT_START_ERROR" ||
            error.response.data.error_code == "AUTH_ERROR"
        ) {
            console.log(error.response.data.error_message);
        } else {
            Message.error(error.response.data.error_message);
        }

        return Promise.reject(error);
    }
);

export default http;